import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import {
  BsArrowLeft,
  BsArrowRight,
  BsBoxSeam,
  BsCalendar3,
  BsCamera,
  BsChatLeftText,
  BsImages,
  BsPeople,
  BsPlusLg,
  BsTrash,
  BsWrench,
  BsXLg,
} from "react-icons/bs";

import ModalSlideOver from "../cardmodal/ModalSlideOver";

import {
  serviceAddScaleRepair,
  serviceAddScaleRepairParts,
} from "../../api/serviceApi";

import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";

import { useUsers } from "../../hooks/useUsers";
import { useProducts } from "../../hooks/useProducts";

import { uuid } from "../../utils/uuid";
import { getImageUrl } from "../../utils/helper";

import "./jobsheetdrawerrepair.css";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

// ============================================================
// HELPERS
// ============================================================

const getId = (item) => item?.id || item?._id || "";

const getUserLabel = (user) => {
  if (user?.name) return user.name;
  if (user?.fullName) return user.fullName;
  if (user?.username) return user.username;
  if (user?.email) return user.email;

  return getId(user);
};

const getProductLabel = (product) => {
  const mpn = product?.mpn || "";
  const name = product?.name || "";

  return [mpn, name].filter(Boolean).join(" — ") || getId(product);
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const emptyPart = () => ({
  productId: "",
  quantity: 1,
  comment: "",
});

const isImageFile = (file) => {
  if (file.type?.startsWith("image/")) {
    return true;
  }

  return /\.(jpg|jpeg|png|webp|heic|heif|avif)$/i.test(file.name || "");
};

const releaseImagePreviews = (images) => {
  if (!Array.isArray(images)) {
    return;
  }

  images.forEach((image) => {
    if (image.type === "local" && image.previewUrl) {
      URL.revokeObjectURL(image.previewUrl);
    }
  });
};

// ============================================================
// COMPONENT
// ============================================================

const JobSheetDrawerRepair = ({ open, jobId, onClose, onSaved }) => {
  const imagesRef = useRef([]);

  // Shared hooks
  const { users, loadingUsers } = useUsers(open);

  const { products, loadingProducts } = useProducts(open);

  const [submitting, setSubmitting] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(null);

  // Repair details
  const [repairerIds, setRepairerIds] = useState([]);

  const [repairDate, setRepairDate] = useState(todayInput());

  // Photos
  const [repairImages, setRepairImages] = useState([]);

  // Repair comments are now an explicit array
  const [repairComments, setRepairComments] = useState([]);

  const [commentDraft, setCommentDraft] = useState("");

  // Parts
  const [partForm, setPartForm] = useState(emptyPart());

  const [repairParts, setRepairParts] = useState([]);

  /*
   * Set only when the repair has already
   * been created but one or more parts failed.
   */
  const [createdRepairId, setCreatedRepairId] = useState("");

  // Enlarged photo viewer
  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);

  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);

  const repairAlreadyCreated = Boolean(createdRepairId);

  const loadingLists = loadingUsers || loadingProducts;

  // ==========================================================
  // KEEP IMAGE REF
  // ==========================================================

  useEffect(() => {
    imagesRef.current = repairImages;
  }, [repairImages]);

  useEffect(() => {
    return () => {
      releaseImagePreviews(imagesRef.current);
    };
  }, []);

  // ==========================================================
  // RESET DRAWER
  // ==========================================================

  useEffect(() => {
    if (!open) {
      return;
    }

    setRepairerIds([]);
    setRepairDate(todayInput());

    setRepairImages((current) => {
      releaseImagePreviews(current);
      return [];
    });

    setRepairComments([]);
    setCommentDraft("");

    setPartForm(emptyPart());
    setRepairParts([]);

    setCreatedRepairId("");
    setUploadProgress(null);

    setEnlargedImageUrl(null);
    setEnlargedImageIndex(0);
  }, [open]);

  // ==========================================================
  // SELECTED REPAIRERS
  // ==========================================================

  const selectedUsers = useMemo(() => {
    return users.filter((user) => repairerIds.includes(getId(user)));
  }, [users, repairerIds]);

  const addRepairer = (userId) => {
    if (!userId || submitting || repairAlreadyCreated) {
      return;
    }

    setRepairerIds((current) => {
      if (current.includes(userId)) {
        return current;
      }

      return [...current, userId];
    });
  };

  const removeRepairer = (userId) => {
    if (submitting || repairAlreadyCreated) {
      return;
    }

    setRepairerIds((current) => current.filter((id) => id !== userId));
  };

  // ==========================================================
  // REPAIR IMAGES
  // ==========================================================

  const handleRepairImages = (event) => {
    const files = Array.from(event.target.files || []);

    /*
     * Allows the same photo/file to
     * be selected again later.
     */
    event.target.value = "";

    if (files.length === 0 || repairAlreadyCreated) {
      return;
    }

    const validFiles = [];

    let invalidFiles = 0;
    let oversizedFiles = 0;

    files.forEach((file) => {
      if (!isImageFile(file)) {
        invalidFiles += 1;
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        oversizedFiles += 1;
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles > 0) {
      toast.error(
        `${invalidFiles} file${
          invalidFiles === 1 ? "" : "s"
        } skipped because they were not valid images.`,
      );
    }

    if (oversizedFiles > 0) {
      toast.error(
        `${oversizedFiles} image${
          oversizedFiles === 1 ? "" : "s"
        } skipped because the maximum size is 5MB.`,
      );
    }

    if (validFiles.length === 0) {
      return;
    }

    const newImages = validFiles.map((file) => ({
      localId: uuid(),
      type: "local",
      file,
      previewUrl: URL.createObjectURL(file),
      comment: "",
    }));

    setRepairImages((current) => [...current, ...newImages]);
  };

  const updateRepairImageComment = (localId, value) => {
    setRepairImages((current) =>
      current.map((image) =>
        image.localId === localId
          ? {
              ...image,
              comment: value,
            }
          : image,
      ),
    );
  };

  const removeRepairImage = (localId) => {
    if (submitting || repairAlreadyCreated) {
      return;
    }

    setRepairImages((current) => {
      const image = current.find((item) => item.localId === localId);

      if (image?.type === "local" && image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }

      return current.filter((image) => image.localId !== localId);
    });
  };

  // ==========================================================
  // REPAIR COMMENTS
  // ==========================================================

  const addRepairComment = () => {
    if (submitting || repairAlreadyCreated) {
      return;
    }

    const comment = commentDraft.trim();

    if (!comment) {
      return;
    }

    setRepairComments((current) => [
      ...current,
      {
        localId: uuid(),
        text: comment,
      },
    ]);

    setCommentDraft("");
  };

  const removeRepairComment = (localId) => {
    if (submitting || repairAlreadyCreated) {
      return;
    }

    setRepairComments((current) =>
      current.filter((comment) => comment.localId !== localId),
    );
  };

  // ==========================================================
  // SELECTED PART PRODUCT
  // ==========================================================

  const selectedPartProduct = useMemo(() => {
    return products.find((product) => getId(product) === partForm.productId);
  }, [products, partForm.productId]);

  const handlePartField = (event) => {
    const { name, value } = event.target;

    setPartForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const partFormValid =
    Boolean(partForm.productId) &&
    Number.isInteger(Number(partForm.quantity)) &&
    Number(partForm.quantity) > 0;

  // ==========================================================
  // ADD PART LOCALLY
  // ==========================================================

  const addPart = () => {
    if (!partFormValid || !selectedPartProduct) {
      toast.error("Complete the repair part details.");

      return;
    }

    setRepairParts((current) => [
      ...current,
      {
        productId: partForm.productId,

        name: selectedPartProduct.name || "",

        imageUrl: selectedPartProduct.imageUrl || "",

        quantity: Number(partForm.quantity),

        comment: partForm.comment.trim(),

        localId: uuid(),
      },
    ]);

    setPartForm(emptyPart());
  };

  const removePart = (localId) => {
    if (submitting) {
      return;
    }

    setRepairParts((current) =>
      current.filter((part) => part.localId !== localId),
    );
  };

  // ==========================================================
  // VALIDATION
  // ==========================================================

  const isValid =
    repairAlreadyCreated || (repairerIds.length > 0 && Boolean(repairDate));

  // ==========================================================
  // UPLOAD REPAIR IMAGES
  // ==========================================================

  const uploadRepairImages = async (uploadedThisAttempt) => {
    if (repairImages.length === 0) {
      return [];
    }

    const finalImages = [];

    setUploadProgress({
      completed: 0,
      total: repairImages.length,
    });

    let completed = 0;

    for (const image of repairImages) {
      const safeJobId = String(jobId || "job").replace(/[^A-Za-z0-9_-]/g, "_");

      const filename = [
        safeJobId,
        "repair",
        Date.now(),
        Math.random().toString(36).slice(2, 8),
      ].join("-");

      const result = await serviceUploadPhoto({
        file: image.file,
        folder: "tims/repairs",
        filename,
      });

      if (!result?.url) {
        throw new Error("Repair image upload failed.");
      }

      uploadedThisAttempt.push(result.url);

      finalImages.push({
        imageUrl: result.url,

        comment: image.comment.trim(),
      });

      completed += 1;

      setUploadProgress({
        completed,
        total: repairImages.length,
      });
    }

    return finalImages;
  };

  // ==========================================================
  // CLEAN FAILED UPLOADS
  // ==========================================================

  const cleanupUploadedPhotos = async (urls) => {
    if (!urls.length) {
      return;
    }

    await Promise.allSettled(urls.map((url) => serviceDeletePhoto(url)));
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async () => {
    if (!isValid || submitting) {
      return;
    }

    let repairId = createdRepairId;

    let repairSaved = Boolean(createdRepairId);

    const uploadedThisAttempt = [];

    try {
      setSubmitting(true);

      // ------------------------------------------------------
      // FIRST CREATE REPAIR
      // ------------------------------------------------------

      if (!repairId) {
        const uploadedImages = await uploadRepairImages(uploadedThisAttempt);

        const payload = {
          repairerIds,
          repairDate,

          repairImages: uploadedImages,

          comments: repairComments
            .map((comment) => comment.text.trim())
            .filter(Boolean),
        };

        const createdRepair = await serviceAddScaleRepair(jobId, payload);

        repairId = getId(createdRepair);

        if (!repairId) {
          throw new Error("Repair was created without a repair ID.");
        }

        /*
         * From this point onwards the repair
         * owns these uploaded images.
         *
         * Do NOT delete them if adding a
         * repair part later fails.
         */
        repairSaved = true;

        setCreatedRepairId(repairId);
      }

      // ------------------------------------------------------
      // NO PARTS
      // ------------------------------------------------------

      if (repairParts.length === 0) {
        toast.success("Repair added successfully.");

        onSaved?.();

        return;
      }

      // ------------------------------------------------------
      // SAVE PARTS
      // ------------------------------------------------------

      const failedParts = [];

      let savedParts = 0;

      for (const part of repairParts) {
        try {
          await serviceAddScaleRepairParts(repairId, {
            productId: part.productId,

            name: part.name,

            imageUrl: part.imageUrl,

            quantity: part.quantity,

            comment: part.comment,
          });

          savedParts += 1;
        } catch (error) {
          console.error("Failed to add repair part:", error);

          failedParts.push(part);
        }
      }

      // ------------------------------------------------------
      // SOME PARTS FAILED
      // ------------------------------------------------------

      if (failedParts.length > 0) {
        setRepairParts(failedParts);

        toast.error(
          `Repair created. ${savedParts} part(s) added, ${failedParts.length} failed. Retry the remaining parts.`,
        );

        return;
      }

      toast.success("Repair and repair parts added successfully.");

      onSaved?.();
    } catch (error) {
      console.error("Error creating repair:", error);

      /*
       * Only clean newly uploaded images
       * if the actual repair was NOT saved.
       */
      if (!repairSaved) {
        await cleanupUploadedPhotos(uploadedThisAttempt);
      }

      if (repairSaved) {
        toast.error(
          error?.message ||
            "Repair was created, but repair parts could not be completed.",
        );
      } else {
        toast.error(error?.message || "Failed to create repair.");
      }
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }
  };

  // ==========================================================
  // IMAGE VIEWER
  // ==========================================================

  const allRepairImages = useMemo(() => {
    return repairImages.map((image, index) => ({
      ...image,
      index,

      src:
        image.type === "local"
          ? image.previewUrl
          : getImageUrl(image.imageUrl || image.url),
    }));
  }, [repairImages]);

  const openImageViewer = (localId) => {
    const index = allRepairImages.findIndex(
      (image) => image.localId === localId,
    );

    if (index === -1) {
      return;
    }

    setEnlargedImageIndex(index);

    setEnlargedImageUrl(allRepairImages[index].src);
  };

  const closeImageViewer = () => {
    setEnlargedImageUrl(null);
    setEnlargedImageIndex(0);
  };

  const showPreviousImage = () => {
    if (allRepairImages.length === 0) {
      closeImageViewer();
      return;
    }

    const newIndex =
      enlargedImageIndex <= 0
        ? allRepairImages.length - 1
        : enlargedImageIndex - 1;

    setEnlargedImageIndex(newIndex);

    setEnlargedImageUrl(allRepairImages[newIndex].src);
  };

  const showNextImage = () => {
    if (allRepairImages.length === 0) {
      closeImageViewer();
      return;
    }

    const newIndex =
      enlargedImageIndex >= allRepairImages.length - 1
        ? 0
        : enlargedImageIndex + 1;

    setEnlargedImageIndex(newIndex);

    setEnlargedImageUrl(allRepairImages[newIndex].src);
  };

  const enlargedCurrentImage = allRepairImages[enlargedImageIndex] || null;

  // Keyboard viewer controls

  useEffect(() => {
    if (!enlargedImageUrl) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeImageViewer();
      }

      if (event.key === "ArrowLeft") {
        showPreviousImage();
      }

      if (event.key === "ArrowRight") {
        showNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enlargedImageUrl, enlargedImageIndex, allRepairImages]);

  useEffect(() => {
    if (!open) {
      closeImageViewer();
    }
  }, [open]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <>
      <ModalSlideOver
        open={open}
        title={repairAlreadyCreated ? "Complete Repair Parts" : "Add Repair"}
        onClose={() => {
          if (!submitting) {
            onClose?.();
          }
        }}
        footer={
          <>
            <button
              type="button"
              className="job-repair-btn ghost"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="button"
              className="job-repair-btn primary"
              disabled={!isValid || submitting}
              onClick={handleSubmit}
            >
              {submitting
                ? uploadProgress
                  ? `Uploading ${uploadProgress.completed}/${uploadProgress.total}...`
                  : "Saving..."
                : repairAlreadyCreated
                  ? "Retry Remaining Parts"
                  : "Save Repair"}
            </button>
          </>
        }
      >
        <div className="job-repair-drawer">
          {repairAlreadyCreated && (
            <div className="job-repair-warning">
              <BsWrench />

              <div>
                <strong>Repair already saved</strong>

                <span>
                  The repair has already been created. Only the remaining repair
                  parts will be retried.
                </span>
              </div>
            </div>
          )}

          {/* ========== REPAIR DETAILS ========== */}
          <section className="job-repair-section">
            <div className="job-repair-section-header">
              <div className="job-repair-section-icon">
                <BsCalendar3 />
              </div>

              <div>
                <h3>Repair Details</h3>

                <p>
                  Set the repair date and select the technicians who completed
                  the work.
                </p>
              </div>
            </div>

            <div className="job-repair-part-grid">
              <div className="job-repair-field">
                <label htmlFor="repair-date">
                  Repair Date <span>*</span>
                </label>

                <input
                  id="repair-date"
                  type="date"
                  value={repairDate}
                  disabled={submitting || repairAlreadyCreated}
                  onChange={(event) => setRepairDate(event.target.value)}
                />
              </div>

              <div className="job-repair-field">
                <label>
                  Repairers <span>*</span>
                </label>

                {loadingUsers ? (
                  <div className="job-repair-loading">Loading users...</div>
                ) : users.length === 0 ? (
                  <div className="job-repair-loading">No users available.</div>
                ) : (
                  <select
                    value=""
                    disabled={submitting || repairAlreadyCreated}
                    onChange={(event) => addRepairer(event.target.value)}
                  >
                    <option value="" disabled>
                      Select a repairer...
                    </option>

                    {users.map((user) => {
                      const id = getId(user);

                      const selected = repairerIds.includes(id);

                      return (
                        <option key={id} value={id} disabled={selected}>
                          {getUserLabel(user)}
                          {selected ? " - Selected" : ""}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            </div>

            {selectedUsers.length > 0 && (
              <div className="job-repair-selected">
                <div className="job-repair-selected-title">
                  <BsPeople />

                  <span>Selected Repairers</span>
                </div>

                <div className="job-repair-selected-list">
                  {selectedUsers.map((user) => {
                    const id = getId(user);

                    return (
                      <span key={id} className="job-repair-selected-item">
                        {getUserLabel(user)}

                        {!repairAlreadyCreated && (
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => removeRepairer(id)}
                            aria-label={`Remove ${getUserLabel(user)}`}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* ========== REPAIR COMMENTS ========== */}
          <section className="job-repair-section">
            <div className="job-repair-section-header">
              <div className="job-repair-section-icon">
                <BsChatLeftText />
              </div>

              <div>
                <h3>Repair Comments</h3>

                <p>Add individual notes about the repair work performed.</p>
              </div>
            </div>

            {repairComments.length > 0 && (
              <div className="job-repair-comments">
                {repairComments.map((comment, index) => (
                  <div key={comment.localId} className="job-repair-comment">
                    <span className="job-repair-comment-number">
                      {index + 1}
                    </span>

                    <span className="job-repair-comment-text">
                      {comment.text}
                    </span>

                    {!repairAlreadyCreated && (
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => removeRepairComment(comment.localId)}
                        aria-label="Remove comment"
                      >
                        <BsTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!repairAlreadyCreated && (
              <div className="job-repair-comment-add">
                <textarea
                  rows="3"
                  value={commentDraft}
                  disabled={submitting}
                  placeholder="Enter a repair comment..."
                  onChange={(event) => setCommentDraft(event.target.value)}
                />

                <button
                  type="button"
                  className="job-repair-btn ghost"
                  disabled={submitting || !commentDraft.trim()}
                  onClick={addRepairComment}
                >
                  <BsPlusLg />
                  Add Comment
                </button>
              </div>
            )}

            {repairComments.length === 0 && repairAlreadyCreated && (
              <div className="job-repair-empty">No repair comments.</div>
            )}
          </section>

          {/* ========== REPAIR PARTS ========== */}
          <section className="job-repair-section">
            <div className="job-repair-section-header">
              <div className="job-repair-section-icon">
                <BsBoxSeam />
              </div>

              <div>
                <h3>Repair Parts</h3>

                <p>Parts are added after the repair receives its repair ID.</p>
              </div>
            </div>

            <div className="job-repair-part-form">
              <div className="job-repair-part-grid">
                <div className="job-repair-field">
                  <label htmlFor="repair-product">Product</label>

                  <select
                    id="repair-product"
                    name="productId"
                    value={partForm.productId}
                    disabled={loadingProducts || submitting}
                    onChange={handlePartField}
                  >
                    <option value="">
                      {loadingProducts
                        ? "Loading products..."
                        : "Select product..."}
                    </option>

                    {products.map((product) => {
                      const id = getId(product);

                      return (
                        <option key={id} value={id}>
                          {getProductLabel(product)}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="job-repair-field">
                  <label htmlFor="repair-part-quantity">Quantity</label>

                  <input
                    id="repair-part-quantity"
                    type="number"
                    name="quantity"
                    min="1"
                    step="1"
                    value={partForm.quantity}
                    disabled={submitting}
                    onChange={handlePartField}
                  />
                </div>
              </div>

              {selectedPartProduct && (
                <div className="job-repair-product-preview">
                  <div className="job-repair-product-image-wrap">
                    {selectedPartProduct.imageUrl ? (
                      <img
                        src={getImageUrl(selectedPartProduct.imageUrl)}
                        alt={selectedPartProduct.name || "Product"}
                      />
                    ) : (
                      <BsBoxSeam />
                    )}
                  </div>

                  <div>
                    <strong>
                      {selectedPartProduct.name || "Unnamed Product"}
                    </strong>

                    {selectedPartProduct.mpn && (
                      <span>MPN: {selectedPartProduct.mpn}</span>
                    )}

                    {selectedPartProduct.shelfAddress && (
                      <span>Shelf: {selectedPartProduct.shelfAddress}</span>
                    )}

                    <span>Stock: {selectedPartProduct.quantity ?? "—"}</span>
                  </div>
                </div>
              )}

              <div className="job-repair-part-grid">
                <div className="job-repair-field">
                  <label htmlFor="repair-part-comment">Comment</label>

                  <textarea
                    id="repair-part-comment"
                    rows="3"
                    name="comment"
                    value={partForm.comment}
                    disabled={submitting}
                    placeholder="Optional note..."
                    onChange={handlePartField}
                  />
                </div>

                <div className="job-repair-add-part-action">
                  <button
                    type="button"
                    className="job-repair-btn primary"
                    disabled={!partFormValid || submitting}
                    onClick={addPart}
                  >
                    <BsPlusLg />
                    Add Part
                  </button>
                </div>
              </div>
            </div>

            {repairParts.length > 0 ? (
              <div className="job-repair-table-scroll">
                <table className="job-repair-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Comment</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {repairParts.map((part) => (
                      <tr key={part.localId}>
                        <td>
                          <div className="job-repair-part-product">
                            <div className="job-repair-part-image-wrap">
                              {part.imageUrl ? (
                                <img
                                  src={getImageUrl(part.imageUrl)}
                                  alt={part.name}
                                />
                              ) : (
                                <BsBoxSeam />
                              )}
                            </div>

                            <strong>{part.name || part.productId}</strong>
                          </div>
                        </td>

                        <td>
                          <span className="job-repair-quantity">
                            {part.quantity}
                          </span>
                        </td>

                        <td>{part.comment || "—"}</td>

                        <td>
                          <button
                            type="button"
                            className="job-repair-remove-part"
                            disabled={submitting}
                            onClick={() => removePart(part.localId)}
                            aria-label="Remove part"
                          >
                            <BsTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="job-repair-empty">No repair parts added.</div>
            )}
          </section>

          {/* ================================================
              REPAIR PHOTOS
          ================================================= */}

          <section className="job-repair-section">
            <div className="job-repair-section-header">
              <div className="job-repair-section-icon">
                <BsImages />
              </div>

              <div>
                <h3>Repair Photos</h3>

                <p>
                  Take photos from a phone camera or select multiple photos.
                  Each photo can have its own optional comment.
                </p>
              </div>
            </div>

            {repairImages.length > 0 ? (
              <div className="job-repair-photo-grid">
                {repairImages.map((image, index) => (
                  <RepairPhoto
                    key={image.localId}
                    image={image}
                    index={index}
                    disabled={submitting || repairAlreadyCreated}
                    onView={openImageViewer}
                    onRemove={removeRepairImage}
                    onCommentChange={updateRepairImageComment}
                  />
                ))}
              </div>
            ) : (
              <div className="job-repair-photo-empty">
                <BsImages />

                <strong>No repair photos</strong>

                <span>Add photos showing the issue or completed repair.</span>
              </div>
            )}

            {!repairAlreadyCreated && (
              <div className="job-repair-photo-actions">
                {/* CAMERA */}

                <label
                  htmlFor="repair-camera"
                  className={`job-repair-photo-btn ${
                    submitting ? "disabled" : ""
                  }`}
                >
                  <BsCamera />
                  Take Photo
                </label>

                <input
                  id="repair-camera"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="job-repair-photo-input"
                  disabled={submitting}
                  onChange={handleRepairImages}
                />

                {/* GALLERY */}

                <label
                  htmlFor="repair-gallery"
                  className={`job-repair-photo-btn ${
                    submitting ? "disabled" : ""
                  }`}
                >
                  <BsImages />
                  Choose Photos
                </label>

                <input
                  id="repair-gallery"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
                  multiple
                  className="job-repair-photo-input"
                  disabled={submitting}
                  onChange={handleRepairImages}
                />
              </div>
            )}
          </section>
        </div>
      </ModalSlideOver>

      {/* ================================================
          ENLARGED PHOTO VIEWER
      ================================================= */}

      {enlargedImageUrl &&
        createPortal(
          <div
            className="job-repair-image-viewer"
            role="dialog"
            aria-modal="true"
            aria-label="Repair photo"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeImageViewer();
              }
            }}
          >
            <div className="job-repair-image-viewer-panel">
              <div className="job-repair-image-viewer-header">
                <div>
                  <h3>Repair Photo</h3>

                  <span>
                    Photo {enlargedImageIndex + 1} of {allRepairImages.length}
                  </span>
                </div>

                <button
                  type="button"
                  className="job-repair-image-viewer-close"
                  onClick={closeImageViewer}
                  aria-label="Close image viewer"
                >
                  <BsXLg />
                </button>
              </div>

              <div className="job-repair-image-viewer-content">
                {allRepairImages.length > 1 && (
                  <button
                    type="button"
                    className="job-repair-image-viewer-nav previous"
                    onClick={showPreviousImage}
                    aria-label="Previous image"
                  >
                    <BsArrowLeft />
                  </button>
                )}

                <img
                  src={enlargedImageUrl}
                  alt={`Repair photo ${enlargedImageIndex + 1}`}
                />

                {allRepairImages.length > 1 && (
                  <button
                    type="button"
                    className="job-repair-image-viewer-nav next"
                    onClick={showNextImage}
                    aria-label="Next image"
                  >
                    <BsArrowRight />
                  </button>
                )}
              </div>

              <div className="job-repair-image-viewer-footer">
                <div>
                  <strong>Repair Photo {enlargedImageIndex + 1}</strong>

                  <span>
                    {enlargedCurrentImage?.comment?.trim() ||
                      "No photo comment"}
                  </span>
                </div>

                <span className="job-repair-image-viewer-count">
                  {enlargedImageIndex + 1} / {allRepairImages.length}
                </span>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// ============================================================
// REPAIR PHOTO
// ============================================================

const RepairPhoto = ({
  image,
  index,
  disabled,
  onView,
  onRemove,
  onCommentChange,
}) => {
  const src =
    image.type === "local"
      ? image.previewUrl
      : getImageUrl(image.imageUrl || image.url);

  return (
    <div className="job-repair-photo-card">
      <div className="job-repair-photo">
        <button
          type="button"
          className="job-repair-photo-view"
          onClick={() => onView(image.localId)}
          aria-label={`View repair photo ${index + 1}`}
        >
          <img src={src} alt={`Repair ${index + 1}`} />
        </button>

        {!disabled && (
          <button
            type="button"
            className="job-repair-photo-remove"
            onClick={() => onRemove(image.localId)}
            aria-label="Remove repair photo"
          >
            <BsTrash />
          </button>
        )}

        <span className="job-repair-photo-number">{index + 1}</span>
      </div>

      <div className="job-repair-photo-comment">
        <label htmlFor={`repair-photo-comment-${image.localId}`}>
          Photo Comment
        </label>

        <textarea
          id={`repair-photo-comment-${image.localId}`}
          rows="2"
          value={image.comment}
          disabled={disabled}
          placeholder="Optional comment for this photo..."
          onChange={(event) =>
            onCommentChange(image.localId, event.target.value)
          }
        />
      </div>
    </div>
  );
};

export default JobSheetDrawerRepair;
